import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    MenuItem,
    Typography,
    useTheme,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Print, Refresh, Edit, Paid } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "services/api";

const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "";

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
        return dateString;
    }

    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
};

const convertToYYYYMMDD = (dateString) => {
    if (!dateString) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }

    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
};

const getDefaultMonthDate = () => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return lastDay.toISOString().split("T")[0];
};

const getStatusLabel = (row) => {
    if (row.paymentStatus === "payé" || row.ligneStatus === "paye") {
        return "Payé";
    }

    if (row.paymentStatus === "impayé" || row.ligneStatus === "impaye") {
        return "Impayé";
    }

    if (row.paymentStatus === "annule") {
        return "Annulé";
    }

    if (row.ligneStatus === "a_relever") {
        return "À relever";
    }

    if (row.ligneStatus === "erreur") {
        return "Erreur";
    }

    return row.ligneStatus || "Non saisi";
};

const getStatusColor = (row) => {
    if (row.paymentStatus === "payé" || row.ligneStatus === "paye") {
        return "success";
    }

    if (row.paymentStatus === "impayé" || row.ligneStatus === "impaye") {
        return "error";
    }

    if (row.ligneStatus === "erreur") {
        return "warning";
    }

    return "default";
};

const RegistreMensuel = () => {
    const theme = useTheme();

    const [villages, setVillages] = useState([]);
    const [village, setVillage] = useState("");
    const [selectedDate, setSelectedDate] = useState(getDefaultMonthDate());

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [editCurrentIndex, setEditCurrentIndex] = useState("");
    const [editObservation, setEditObservation] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");

    const formattedSelectedDate = formatDateToDDMMYYYY(selectedDate);

    const filteredRows = rows.filter((row) => {
        const isPaid = row.paymentStatus === "payé" || row.ligneStatus === "paye";

        if (statusFilter === "all") return true;
        if (statusFilter === "payé") return isPaid;
        if (statusFilter === "impayé") return !isPaid;

        return true;
    });

    const handleMarkSelectedPaid = async () => {
        console.log("selectedIds au clic:", selectedIds);
        const ligneIds = selectedIds.map(Number);

        if (ligneIds.length === 0) {
            toast.info("Veuillez sélectionner au moins une ligne.");
            return;
        }

        const confirmed = window.confirm(
            `Marquer ${ligneIds.length} ligne(s) comme payée(s) ?`
        );

        if (!confirmed) return;

        try {
            setIsLoading(true);

            const response = await api.put("/api/registre-mensuel/mark-paid-bulk", {
                ligneIds,
            });

            toast.success(response.data.message || "Paiements enregistrés.");
            setSelectedIds([]);
            await fetchRegistre();
        } catch (error) {
            console.error("Erreur paiement multiple:", error);
            toast.error(
                error.response?.data?.error || "Impossible de marquer comme payé."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const fetchVillages = async () => {
        try {
            const response = await api.get("/api/clients");
            const uniqueVillages = [
                ...new Set(response.data.map((client) => client.village).filter(Boolean)),
            ];
            setVillages(uniqueVillages);
        } catch (error) {
            console.error("Erreur chargement villages:", error);
            toast.error("Impossible de charger les villages.");
        }
    };

    const fetchRegistre = async () => {
        if (!village || !formattedSelectedDate) {
            toast.info("Veuillez sélectionner un village et une date.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.get("/api/registre-mensuel", {
                params: {
                    selectedDate: formattedSelectedDate,
                    village,
                },
            });

            const dataWithIds = response.data
                .map((item) => ({
                    ...item,
                    id: item.ligneId,
                }))
                .sort((a, b) =>
                    (a.waterBillRefNumber || "").localeCompare(
                        b.waterBillRefNumber || "",
                        undefined,
                        { numeric: true }
                    )
                );
            setRows(dataWithIds);
            toast.success(`${response.data.length} lignes chargées.`);
        } catch (error) {
            console.error("Erreur chargement registre:", error);
            toast.error("Impossible de charger le registre.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (village && selectedDate) {
            fetchRegistre();
        }
    }, [village, selectedDate]);

    const prepareRegistre = async () => {
        if (!village || !formattedSelectedDate) {
            toast.info("Veuillez sélectionner un village et une date.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post("/api/registre-mensuel/prepare", {
                selectedDate: formattedSelectedDate,
                village,
            });

            const { inserted, skipped } = response.data;

            if (inserted === 0 && skipped > 0) {
                toast.info("Ce registre existe déjà. Chargement des données.");
            } else {
                toast.success(response.data?.message || "Registre préparé.");
            }

            await fetchRegistre();
        } catch (error) {
            console.error("Erreur préparation registre:", error);
            toast.error("Impossible de préparer le registre.");
        } finally {
            setIsLoading(false);
        }
    };
  
    const handlePrint = () => {
        if (!village || !formattedSelectedDate) {
            toast.info("Veuillez sélectionner un village et un mois.");
            return;
        }

        if (rows.length === 0) {
            toast.info("Veuillez d'abord cliquer sur « Préparer » pour générer le registre.");
            return;
        }
        const printWindow = window.open("", "_blank");

        printWindow.document.write(`
      <html>
        <head>
          <title>Registre Mensuel</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 30px; }
            h1, h2 { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #000; padding: 6px; font-size: 12px; }
            th { background: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>Registre Mensuel de Consommation</h1>
          <h2>Village : ${village} — Mois : ${formattedSelectedDate}</h2>

          <table>
            <thead>
              <tr>
                <th>Réf. Client</th>
                <th>Réf. Facture Mensuelle</th>
                <th>Prénom</th>
                <th>Nom</th>
                <th>Type</th>
                <th>Ancien Index</th>
                <th>Nouvel Index</th>
                <th>Observation</th>
              </tr>
            </thead>
            <tbody>
    `);

        rows.forEach((row) => {
            printWindow.document.write(`
        <tr>
          <td>${row.waterBillRefNumber || ""}</td>
          <td>${row.monthlyBillRefNumber || ""}</td>
          <td>${row.firstName || ""}</td>
          <td>${row.lastName || ""}</td>
          <td>${row.typeBranchement || ""}</td>
          <td>${row.previousIndex || ""}</td>
          <td>${row.currentIndex || row.currentIndexSnapshot || ""}</td>
          <td>${row.observation || ""}</td>
        </tr>
      `);
        });

        printWindow.document.write(`
            </tbody>
          </table>
        </body>
      </html>
    `);

        printWindow.document.close();
        printWindow.print();
    };

    useEffect(() => {
        fetchVillages();
    }, []);

    const handleOpenEdit = (row) => {
        setSelectedRow(row);
        setEditCurrentIndex(row.currentIndex ?? row.currentIndexSnapshot ?? "");
        setEditObservation(row.observation || "");
        setOpenEditDialog(true);
    };

    const handleSaveEdit = async () => {
        if (!selectedRow) return;

        const previousIndex = parseFloat(selectedRow.previousIndex || 0);
        const newIndex = parseFloat(editCurrentIndex || 0);

        if (newIndex < previousIndex) {
            toast.error("Le nouvel index ne peut pas être inférieur à l'ancien index.");
            return;
        }
        const isPaid =
            selectedRow.paymentStatus === "payé" ||
            selectedRow.ligneStatus === "paye";

        if (isPaid) {
            const confirmed = window.confirm(
                "⚠️ Cette facture est déjà marquée comme payée.\n\n" +
                "La modification mettra automatiquement à jour les montants et le paiement enregistré.\n\n" +
                "Voulez-vous continuer ?"
            );

            if (!confirmed) {
                return;
            }
        }

        try {
            await api.put(`/api/registre-mensuel/${selectedRow.ligneId}`, {
                currentIndexSnapshot: newIndex,
                observation: editObservation,
                ligneStatus: "releve_effectue",
            });

            if (selectedRow.consommationIdFromTable) {
                const consommation = newIndex - previousIndex;
                const price = parseFloat(selectedRow.price || 200);
                const duePayment = consommation * price;

                const finePrice = parseFloat(selectedRow.finePrice || 0);

                const latePayment =
                    selectedRow.paidDate && selectedRow.duePaymentDate
                        ? Math.max(
                            0,
                            Math.floor(
                                (new Date(convertToYYYYMMDD(selectedRow.paidDate)) -
                                    new Date(convertToYYYYMMDD(selectedRow.duePaymentDate))) /
                                (1000 * 60 * 60 * 24)
                            )
                        )
                        : 0;

                const fine = latePayment > 0 ? latePayment * finePrice : 0;

                const isPaid =
                    selectedRow.paymentStatus === "payé" ||
                    selectedRow.ligneStatus === "paye";

                const paidAmount = isPaid
                    ? duePayment + fine
                    : parseFloat(selectedRow.paidAmount || 0);

                await api.put(`/api/consommations/${selectedRow.consommationIdFromTable}`, {
                    currentIndex: newIndex,
                    previousIndex,
                    consommation,
                    price,
                    duePayment,
                    paidAmount,
                    paymentStatus: selectedRow.paymentStatus || "impayé",
                    finePrice,
                    fine,
                    duePaymentDate: selectedRow.duePaymentDate,
                    latePayment,
                    paidDate: selectedRow.paidDate,
                });
            }

            toast.success("Ligne mise à jour.");
            setOpenEditDialog(false);
            await fetchRegistre();
        } catch (error) {
            console.error("Erreur modification registre:", error);
            toast.error("Impossible de modifier la ligne.");
        }
    };

    // const handleSaveEdit = async () => {
    //     if (!selectedRow) return;

    //     const previousIndex = parseFloat(selectedRow.previousIndex || 0);
    //     const newIndex = parseFloat(editCurrentIndex || 0);

    //     if (newIndex < previousIndex) {
    //         toast.error("Le nouvel index ne peut pas être inférieur à l'ancien index.");
    //         return;
    //     }

    //     try {
    //         await api.put(`/api/registre-mensuel/${selectedRow.ligneId}`, {
    //             currentIndexSnapshot: newIndex,
    //             observation: editObservation,
    //             ligneStatus: "releve_effectue",
    //         });

    //         if (selectedRow.consommationIdFromTable) {
    //             const consommation = newIndex - previousIndex;
    //             const price = parseFloat(selectedRow.price || 200);
    //             const duePayment = consommation * price;

    //             const isPaid =
    //                 selectedRow.paymentStatus === "payé" ||
    //                 selectedRow.ligneStatus === "paye";

    //             const paidAmount = isPaid ? duePayment : parseFloat(selectedRow.paidAmount || 0);

    //             await api.put(`/api/consommations/${selectedRow.consommationIdFromTable}`, {
    //                 currentIndex: newIndex,
    //                 previousIndex,
    //                 consommation,
    //                 price,
    //                 duePayment,
    //                 paidAmount,
    //                 paymentStatus: selectedRow.paymentStatus || "impayé",
    //                 finePrice: parseFloat(selectedRow.finePrice || 0),
    //                 fine: parseFloat(selectedRow.fine || 0),
    //                 duePaymentDate: selectedRow.duePaymentDate,
    //                 latePayment: parseInt(selectedRow.latePayment || 0, 10),
    //                 paidDate: selectedRow.paidDate,
    //             });
    //         }

    //         toast.success("Ligne mise à jour.");
    //         setOpenEditDialog(false);
    //         await fetchRegistre();
    //     } catch (error) {
    //         console.error("Erreur modification registre:", error);
    //         toast.error("Impossible de modifier la ligne.");
    //     }
    // };

    const handleMarkPaid = async (row) => {
        if (
            row.paymentStatus === "payé" ||
            row.ligneStatus === "paye"
        ) {
            toast.info("Cette facture est déjà marquée comme payée.");
            return;
        }
        if (!row.consommationIdFromTable) {
            toast.info(
                "Le relevé a été saisi directement dans le registre, mais la consommation n'a pas encore été enregistrée. Le paiement n’est donc pas encore possible."
            );
            return;
        }

        try {
            const paidAmount = parseFloat(row.duePayment || 0);
            const today = new Date();
            const yyyyMMdd = today.toISOString().split("T")[0];
            const paidDate = formatDateToDDMMYYYY(yyyyMMdd);

            await api.put(`/api/consommations/${row.consommationIdFromTable}`, {
                currentIndex: parseFloat(row.currentIndex || row.currentIndexSnapshot || 0),
                previousIndex: parseFloat(row.consommationPreviousIndex || row.previousIndex || 0),
                consommation: parseFloat(row.consommation || 0),
                price: parseFloat(row.price || 200),
                duePayment: parseFloat(row.duePayment || 0),
                paidAmount,
                paymentStatus: "payé",
                finePrice: parseFloat(row.finePrice || 0),
                fine: parseFloat(row.fine || 0),
                duePaymentDate: row.duePaymentDate,
                latePayment: parseInt(row.latePayment || 0, 10),
                paidDate,
            });

            toast.success("Paiement enregistré.");
            await fetchRegistre();
        } catch (error) {
            console.error("Erreur paiement registre:", error);
            toast.error("Impossible de marquer comme payé.");
        }
    };

    const columns = [
        {
            field: "waterBillRefNumber",
            headerName: "Réf. Client",
            width: 130,
        },
        {
            field: "monthlyBillRefNumber",
            headerName: "Réf. Mensuelle",
            width: 170,
        },
        {
            field: "firstName",
            headerName: "Prénom",
            width: 160,
        },
        {
            field: "lastName",
            headerName: "Nom",
            width: 150,
        },
        {
            field: "typeBranchement",
            headerName: "Type",
            width: 190,
        },
        {
            field: "previousIndex",
            headerName: "Ancien Index",
            width: 130,
        },
        {
            field: "currentIndex",
            headerName: "Nouvel Index",
            width: 130,
            valueGetter: (params) =>
                params.row.currentIndex ?? params.row.currentIndexSnapshot ?? "—",
        },
        {
            field: "consommation",
            headerName: "Consommation",
            width: 130,
            valueGetter: (params) => params.row.consommation ?? "—",
        },
        {
            field: "duePayment",
            headerName: "Montant dû",
            width: 130,
            valueGetter: (params) => params.row.duePayment ?? "—",
        },
        {
            field: "paidAmount",
            headerName: "Montant payé",
            width: 130,
            valueGetter: (params) => params.row.paidAmount ?? "—",
        },
        {
            field: "paymentStatus",
            headerName: "Statut",
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={getStatusLabel(params.row)}
                    color={getStatusColor(params.row)}
                    size="small"
                />
            ),
        },
        {
            field: "paidDate",
            headerName: "Date paiement",
            width: 130,
            valueGetter: (params) => params.row.paidDate || "—",
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 180,
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => handleOpenEdit(params.row)}
                    >
                        Modifier
                    </Button>

                    <Button
                        size="small"
                        variant={
                            params.row.paymentStatus === "payé" ||
                                params.row.ligneStatus === "paye"
                                ? "outlined"
                                : "contained"
                        }
                        color={
                            params.row.paymentStatus === "payé" ||
                                params.row.ligneStatus === "paye"
                                ? "inherit"
                                : "success"
                        }
                        startIcon={<Paid />}
                        onClick={() => handleMarkPaid(params.row)}
                    >
                        {params.row.paymentStatus === "payé" ||
                            params.row.ligneStatus === "paye"
                            ? "Déjà payé"
                            : "Payé"}
                    </Button>
                </Box>
            ),
        }
    ];

    return (
        <Box m="20px">
            <Typography variant="h4" fontWeight="bold" mb={2}>
                Registre Mensuel
            </Typography>

            <Box display="flex" gap={2} mb={2} alignItems="center">
                <TextField
                    select
                    label="Village"
                    value={village}
                    onChange={(e) => setVillage(e.target.value)}
                    sx={{ minWidth: 260 }}
                >
                    {villages.map((item) => (
                        <MenuItem key={item} value={item}>
                            {item}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Mois de relevé / saisie"
                    type="date"
                    value={convertToYYYYMMDD(formattedSelectedDate)}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />

                <Button variant="contained" onClick={prepareRegistre} disabled={isLoading}>
                    Préparer
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={fetchRegistre}
                    disabled={isLoading || !village}
                >
                    Rafraîchir
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<Print />}
                    onClick={handlePrint}
                >
                    Imprimer PDF
                </Button>

                <TextField
                    select
                    label="Statut paiement"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="payé">Payé</MenuItem>
                    <MenuItem value="impayé">Impayé</MenuItem>
                </TextField>

                <Button
                    variant="contained"
                    color="success"
                    startIcon={<Paid />}
                    disabled={selectedIds.length === 0 || isLoading}
                    onClick={handleMarkSelectedPaid}
                    sx={{
                        opacity: selectedIds.length > 0 ? 1 : 0.5,
                        fontWeight: "bold",
                    }}
                >
                    Marquer sélection payée
                </Button>
            </Box>

            <Box
                height="75vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.background.alt,
                    },
                    "& .row-paid": {
                        backgroundColor: "rgba(76, 175, 80, 0.14)",
                    },
                    "& .row-unpaid": {
                        backgroundColor: "rgba(244, 67, 54, 0.12)",
                    },
                    "& .row-pending": {
                        backgroundColor: "rgba(158, 158, 158, 0.10)",
                    },
                }}
            >
                {/* <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    getRowId={(row) => row.ligneId}
                    getRowClassName={(params) => {
                        if (
                            params.row.paymentStatus === "payé" ||
                            params.row.ligneStatus === "paye"
                        ) {
                            return "row-paid";
                        }

                        if (
                            params.row.paymentStatus === "impayé" ||
                            params.row.ligneStatus === "impaye"
                        ) {
                            return "row-unpaid";
                        }

                        return "row-pending";
                    }}
                    localeText={{
                        noRowsLabel: village
                            ? "Aucun registre. Cliquez sur 'Préparer' pour générer le registre."
                            : "Veuillez sélectionner un village et un mois.",
                    }}
                /> */}
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={isLoading}
                    checkboxSelection
                    disableSelectionOnClick
                    selectionModel={selectedIds}
                    onSelectionModelChange={(newSelection) => {
                        console.log("SELECTION:", newSelection);
                        setSelectedIds(newSelection);
                    }}
                    getRowId={(row) => row.ligneId}
                />
            </Box>
            <Dialog
                open={openEditDialog}
                onClose={() => setOpenEditDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Modifier la ligne du registre</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Correction du nouvel index et des observations.
                    </DialogContentText>

                    <TextField
                        margin="dense"
                        label="Référence client"
                        fullWidth
                        value={selectedRow?.waterBillRefNumber || ""}
                        disabled
                    />

                    <TextField
                        margin="dense"
                        label="Référence facture mensuelle"
                        fullWidth
                        value={selectedRow?.monthlyBillRefNumber || ""}
                        disabled
                    />

                    <TextField
                        margin="dense"
                        label="Ancien index"
                        type="number"
                        fullWidth
                        value={selectedRow?.previousIndex || 0}
                        disabled
                    />

                    <TextField
                        margin="dense"
                        label="Nouvel index"
                        type="number"
                        fullWidth
                        value={editCurrentIndex}
                        onChange={(e) => setEditCurrentIndex(e.target.value)}
                    />

                    <TextField
                        margin="dense"
                        label="Observation"
                        fullWidth
                        multiline
                        minRows={3}
                        value={editObservation}
                        onChange={(e) => setEditObservation(e.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSaveEdit}>
                        Enregistrer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RegistreMensuel;